import type React from 'react'
import intl from 'react-intl-universal'
import { LOCALE } from '@foxrenderfarm/internals'
import {
  LightDropDownColumns,
  LightDropDownDivider,
  LightNavDropDownWrapper,
  ResourceDropDownColumn,
  ResourceDropDownSection,
  ResourceTitle,
} from '../../atoms'
import {
  LightNavDropDownPanel,
  MainLink,
  Trigger,
  getLocalAppOrigin,
  getResourceSectionTitle,
  isPathActive,
} from './common'
import {
  ResourceBlogIcon,
  ResourceCustomerWorksIcon,
  ResourceEducationDiscountIcon,
  ResourceFreelancerSupportIcon,
  ResourceGetSponsoredIcon,
  ResourceNewsIcon,
  ResourceReferralProgramIcon,
} from '../../svg'

interface ResourcesDropDownProps {
  currentLocale: LOCALE
  currentPathname: string
  origin: string
}

export const ResourcesDropDown = ({
  currentLocale,
  currentPathname,
  origin,
}: ResourcesDropDownProps) => {
  const useGatsby = process.env.GATSBY_PACKAGE_NAME === 'main'
  const useNews = process.env.GATSBY_PACKAGE_NAME === 'news'
  const useShare = process.env.GATSBY_PACKAGE_NAME === 'share'
  const pathLocale = currentLocale === LOCALE.EN ? '' : `/${currentLocale}`
  const mainOrigin = getLocalAppOrigin(origin, 8084)
  const newsOrigin = getLocalAppOrigin(origin, 9000)
  const shareOrigin = getLocalAppOrigin(origin, 8086)
  const contentHub: {
    to?: string
    label: string
    href?: string
    useGatsbyLink?: boolean
    Icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>
  }[] = [
    {
      to: '/share/index/',
      label: intl.get('index.header.resources.blog'),
      href: `${shareOrigin}/share/index/`,
      useGatsbyLink: useShare,
      Icon: ResourceBlogIcon,
    },
    {
      to: '/news/main/',
      label: intl.get('index.header.resources.news'),
      useGatsbyLink: useNews,
      href: `${newsOrigin}/news/main/`,
      Icon: ResourceNewsIcon,
    },
    {
      to: '/client-works.html',
      href: `${mainOrigin}/client-works.html`,
      label: intl.get('index.header.resources.customerWorks'),
      Icon: ResourceCustomerWorksIcon,
    },
  ]
  const programs: {
    label: string
    to: string
    Icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>
  }[] = [
    {
      label: intl.get('index.footer.program.goCloud'),
      to: `${pathLocale}/gocloud-program.html`,
      Icon: ResourceEducationDiscountIcon,
    },
    {
      label: intl.get('index.footer.program.goCreation'),
      to: `${pathLocale}/gocreation-program.html`,
      Icon: ResourceFreelancerSupportIcon,
    },
    {
      label: intl.get('index.footer.program.goSpark'),
      to: `${pathLocale}/free-render-farm.html`,
      Icon: ResourceGetSponsoredIcon,
    },
    {
      label: intl.get('index.footer.program.referral'),
      to: `${pathLocale}/invite.html`,
      Icon: ResourceReferralProgramIcon,
    },
  ]
  const active =
    isPathActive(currentPathname, '/client-works.html', currentLocale) ||
    currentPathname.includes('news/main')

  return (
    <LightNavDropDownWrapper $isActive={active} $panelMinWidth={500}>
      <Trigger>{intl.get('index.header.resources')}</Trigger>
      <LightNavDropDownPanel>
        <LightDropDownColumns style={{ alignItems: 'flex-start', gap: 48 }}>
          <ResourceDropDownColumn>
            <ResourceTitle>{getResourceSectionTitle(currentLocale, 'contentHub')}</ResourceTitle>
            <ResourceDropDownSection>
              {contentHub.map((item) => {
                const Icon = item.Icon
                if (item.to) {
                  return (
                    <MainLink
                      key={item.label}
                      to={item.to}
                      useGatsbyLink={item.useGatsbyLink ?? useGatsby}
                      locale={currentLocale}
                      href={item.href}
                      $highlight={
                        item.to === '/client-works.html'
                          ? isPathActive(currentPathname, item.to, currentLocale)
                          : item.label === 'News'
                            ? currentPathname.includes('news/main')
                            : false
                      }
                    >
                      {Icon && <Icon style={{ marginRight: 8 }} />}
                      {item.label}
                    </MainLink>
                  )
                }
                return null
              })}
            </ResourceDropDownSection>
          </ResourceDropDownColumn>
          <LightDropDownDivider />
          <ResourceDropDownColumn style={{ minWidth: 240 }}>
            <ResourceTitle>{getResourceSectionTitle(currentLocale, 'programsPerks')}</ResourceTitle>
            <ResourceDropDownSection className='right'>
              {programs.map((item) => {
                const Icon = item.Icon
                return (
                  <MainLink
                    key={item.label}
                    to={item.to}
                    href={`${mainOrigin}${item.to}`}
                    useGatsbyLink={useGatsby}
                    locale={currentLocale}
                  >
                    {Icon && <Icon style={{ marginRight: 8 }} />}
                    {item.label}
                  </MainLink>
                )
              })}
            </ResourceDropDownSection>
          </ResourceDropDownColumn>
        </LightDropDownColumns>
      </LightNavDropDownPanel>
    </LightNavDropDownWrapper>
  )
}
